import { useEffect, useRef, useState } from 'react';
import CodeEditorModule from 'react-simple-code-editor';
import Prism from 'prismjs/components/prism-core';
import 'prismjs/components/prism-css';

// react-simple-code-editor is CommonJS and sets __esModule where esbuild can't
//  see it, so a default import lands on the module rather than the component.
//  Without this the editor renders as "element type is invalid".
const CodeEditor = CodeEditorModule.default ?? CodeEditorModule;
import ThemedForm from './ThemedForm.jsx';
import StyleSection from './StylesSection.jsx';
import ColorPickerGrid from '../ColorPickerGrid.jsx';
import { ratio, scoreFromRatio } from '@opendevtools/wcag-color';
import * as Toast from '../../lib/Toast.js';
import ErrorMessageBlock from '../ErrorMessageBlock.jsx';
import Select from '~/components/Select';
import {
    setCssEditorVisible,
    startPickingElement,
    stopPickingElement,
    useIsInspectorReady,
    useIsPickingElement,
} from '~/lib/CssInspectorState';

export const fontSizeOptions = {
    normal: 'Normal',
    small: 'Small',
    large: 'Large',
    'extra-large': 'Extra Large',
};

// Kept in step with CssSanitizer::MAX_LENGTH
const MAX_CSS_LENGTH = 32768;

const PLACEHOLDER_CSS = `.breely-form {
    --breely-color: #7a5af8;
}

.breely-form [data-breely-part='submit'] {
    letter-spacing: 0.08em;
}`;

const colors = [
    '#000000',
    '#007AFF',
    '#34C759',
    '#FFCC00',
    '#FF3B30',
    '#7AC6F5',
    '#AF52DE',
    '#5856D6',
    '#FF2D55',
    '#B51A00',
];

/**
 * Colours the CSS behind the text box. react-simple-code-editor lays a
 *  transparent textarea over this, so both have to be the same size and font —
 *  which is why the font lives on the wrapper and both inherit it.
 */
function highlightCss(code) {
    return Prism.highlight(code, Prism.languages.css, 'css');
}

/**
 * Where a new rule can go without landing inside an existing one.
 *
 * Inserting leaves the caret between the braces, ready to type in, so picking
 *  a second element without typing anything would otherwise nest one rule
 *  inside another and neither would survive being read.
 */
function insertionPointFor(css, caret) {
    const before = css.slice(0, caret);
    const depth =
        (before.match(/{/g) || []).length - (before.match(/}/g) || []).length;

    if (depth <= 0) {
        return caret;
    }

    const closing = css.indexOf('}', caret);

    return closing === -1 ? css.length : closing + 1;
}

/**
 * Every selector that reaches the element just picked, broadest first.
 *
 * The broad one is what gets inserted, because it is what someone usually
 *  wants. The other two are here because the same hook is shared between
 *  questions — selection-block is an appointment type, a staff member, an
 *  add-on and a package — and nothing in the name says which.
 */
const PickedSelectors = ({ picked, onDismiss }) => {
    const [copied, setCopied] = useState(null);

    const options = [
        { selector: picked.selector, caption: 'everywhere on the form' },
        { selector: picked.inQuestionType, caption: 'in this question type' },
        { selector: picked.inQuestion, caption: 'in this question' },
    ].filter(option => !!option.selector);

    function copy(selector) {
        // Not available on every browser or outside a secure context, and the
        //  selectors are selectable text either way
        if (!navigator.clipboard) {
            return;
        }

        navigator.clipboard.writeText(selector).then(
            () => setCopied(selector),
            () => setCopied(null),
        );
    }

    return (
        <div className="mt-2 rounded border border-gray-300 bg-white p-2">
            <div className="flex items-center justify-between">
                <div className="text-gray-500 text-xs">
                    Selectors for what you picked
                </div>
                <button
                    type="button"
                    className="text-gray-500 text-xs underline"
                    onClick={onDismiss}
                >
                    Dismiss
                </button>
            </div>

            {options.map((option, index) => (
                <button
                    key={option.selector}
                    type="button"
                    title="Copy"
                    className="mt-1 block w-full text-left hover:bg-primary-100 rounded px-1 py-0.5"
                    onClick={() => copy(option.selector)}
                >
                    <span
                        className={`font-mono text-xs break-all ${index === 0 ? '' : 'text-gray-500'}`}
                    >
                        {option.selector}
                    </span>
                    <div className="text-gray-500 text-xs">
                        {copied === option.selector ? 'copied' : option.caption}
                        {index === 0 ? ' · added to your CSS' : ''}
                    </div>
                </button>
            ))}
        </div>
    );
};

const CustomCssEditor = ({ styles, setStyles, warnings }) => {
    const css = styles?.customCss || '';
    // react-simple-code-editor's ref hands back its undo history rather than
    //  the element, so the textarea is found inside the wrapper instead. The
    //  caret still matters: picking an element inserts a rule where it is
    const editorRef = useRef(null);
    const textareaOf = () => editorRef.current?.querySelector('textarea');
    const [caret, setCaret] = useState(null);
    // The selectors for whatever was picked last, kept on screen afterwards so
    //  the narrower ones can be read off and used
    const [picked, setPicked] = useState(null);
    const isPicking = useIsPickingElement();
    const isInspectorReady = useIsInspectorReady();

    // While this is on screen, hovering the form in the designer names the
    //  selector for whatever is under the cursor. The Advanced page unmounts
    //  its contents when closed, so mounting is the signal
    useEffect(() => {
        setCssEditorVisible(true);

        return () => setCssEditorVisible(false);
    }, []);

    // Written to on every render so picking always inserts into the current
    //  CSS rather than whatever it was when picking started
    const insertRuleRef = useRef(null);
    insertRuleRef.current = selector => {
        const textarea = textareaOf();
        const caretAt = Math.min(
            textarea?.selectionStart ?? css.length,
            css.length,
        );
        const at = insertionPointFor(css, caretAt);
        const before = css.slice(0, at);
        const after = css.slice(at);

        const separator =
            before.trim() === '' || before.endsWith('\n') ? '' : '\n';
        const rule = `${separator}${selector} {\n    \n}\n`;

        setStyles({ ...styles, customCss: before + rule + after });
        // Inside the braces, where they were about to type anyway
        setCaret(before.length + rule.indexOf('{') + 6);
    };

    // Restoring the caret has to wait for the new value to be rendered
    useEffect(() => {
        if (caret === null) {
            return;
        }

        const textarea = textareaOf();
        textarea?.focus();
        textarea?.setSelectionRange(caret, caret);
        setCaret(null);
    }, [caret]);

    return (
        <div>
            <div className="text-gray-500 text-sm px-2">
                Styles the public version of this form. Rules are scoped to
                the form, so they can't affect the rest of the page. Target
                the <code>data-breely-part</code> attributes on the form,
                and override the <code>--breely-color</code> variables to
                recolour it.
            </div>

            {!!picked && !isPicking && (
                <PickedSelectors
                    picked={picked}
                    onDismiss={() => setPicked(null)}
                />
            )}

            <div
                ref={editorRef}
                className="css-editor mt-2 w-full min-h-48 resize-y font-mono text-xs border rounded bg-white overflow-auto"
            >
                <CodeEditor
                    value={css}
                    onValueChange={value =>
                        setStyles({ ...styles, customCss: value })
                    }
                    highlight={highlightCss}
                    placeholder={PLACEHOLDER_CSS}
                    padding={8}
                    // Fill the wrapper once the user drags it taller, so
                    //  clicking the empty space below the CSS still focuses it.
                    style={{ minHeight: '200px' }}
                />
            </div>

            <div className="flex items-center justify-between px-2 pt-2">
                <button
                    type="button"
                    disabled={!isInspectorReady}
                    className={`text-xs ${isPicking ? 'btn-pill' : 'btn-light-pill'} ${!isInspectorReady ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() =>
                        isPicking
                            ? stopPickingElement()
                            : startPickingElement(selectors => {
                                  insertRuleRef.current(selectors.selector);
                                  setPicked(selectors);
                              })
                    }
                >
                    {isPicking ? 'Cancel' : 'Pick an element'}
                </button>

				<div className="text-gray-500 text-xs">
                    {css.length} / {MAX_CSS_LENGTH} characters
                </div>
            </div>

            {!isInspectorReady && (
                <div className="text-gray-500 text-xs mt-1">
                    Open the Design tab to pick an element from the form.
                </div>
            )}

            {isPicking && (
                <div className="text-gray-500 text-xs mt-1">
                    Click anything in the form to add a rule for it, or press
                    escape to stop.
                </div>
            )}

            {!!warnings?.length && (
                <div className="mt-2">
                    <div className="text-sm font-medium text-red-600">
                        Some of your CSS won't take effect
                    </div>
                    <ul className="mt-1 text-sm text-gray-500 list-disc pl-4">
                        {warnings.map((warning, idx) => (
                            <li key={idx}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};


export const StylesColor = ({ styles, setStyles }) => {
	function getRatio(foreground) {
		try {
			return ratio(foreground, '#f9f9f9');
		} catch (e) {
			return -1;
		}
	}
	const themeColor = styles?.color || '#2C87F3';
	const ratioScore = getRatio(themeColor);
	const minLegibilityRatio = 1.3;

	return (
		<StyleSection>
			{ratioScore < 0 && (
				<ErrorMessageBlock className="mb-4">
					Invalid color, please pick a color or provide a hex code
					like #AABBCC
				</ErrorMessageBlock>
			)}

			{ratioScore > 0 && scoreFromRatio(ratioScore) === 'Fail' && (
				<ErrorMessageBlock className="mb-4">
					This theme color does not provide enough contrast to meet
					accessibility standards.
				</ErrorMessageBlock>
			)}
			<ColorPickerGrid
				onChange={color => {
					if (
						color.length >= 6 &&
						getRatio(color) > 0 &&
						getRatio(color) < minLegibilityRatio
					) {
						Toast.error(
							'Unable to set color, there is not enough contrast with the background for it to be legible to users.',
						);
						return;
					}
					setStyles({ ...styles, color: color });
				}}
				value={themeColor}
			/>
		</StyleSection>
	);
};

export const StylesFont = ({ styles, setStyles }) => {
	return (
		<>
			<StyleSection title="Font">
				<button
					type="button"
					className={`theme-font-serif fake-input ${styles.font === 'serif' ? 'selected' : ''}`}
					onClick={() => setStyles({ ...styles, font: 'serif' })}
				>
					Serif
				</button>
				<button
					type="button"
					className={`theme-font-sans fake-input mt-2 ${styles.font === 'sans' || !styles.font ? 'selected' : ''}`}
					onClick={() => setStyles({ ...styles, font: 'sans' })}
				>
					Sans
				</button>
			</StyleSection>

			<StyleSection title="Font Size" className="mt-4">
				<Select
					value={styles?.fontSize || 'normal'}
					onChange={value => setStyles({ ...styles, fontSize: value })}
					options={fontSizeOptions}
					inputClassName="w-full"
				/>
			</StyleSection>
		</>
	);
};

export const StylesInputs = ({ styles, setStyles }) => {
	return (
		<StyleSection>
			<button
				type="button"
				className={`input-square fake-input ${styles.input === 'square' ? 'selected' : ''}`}
				onClick={() => setStyles({ ...styles, input: 'square' })}
			>
				Square
			</button>
			<button
				type="button"
				className={`input-rounded-rectangle mt-2 fake-input ${styles.input === 'rounded-rectangle' || !styles.input ? 'selected' : ''}`}
				onClick={() =>
					setStyles({ ...styles, input: 'rounded-rectangle' })
				}
			>
				Radius
			</button>
			<button
				type="button"
				className={`input-pill mt-2 fake-input ${styles.input === 'pill' ? 'selected' : ''}`}
				onClick={() => setStyles({ ...styles, input: 'pill' })}
			>
				Pill
			</button>
		</StyleSection>
	);
};

export const StylesButtons = ({ styles, setStyles }) => {
	return (
		<ThemedForm styles={styles} onlyColor={true}>
			<StyleSection>
				<div className="grid grid-cols-2 gap-x-4 gap-y-3">
					<button
						type="button"
						className={`form-button button-square themed-button block w-32 ${styles.button === 'square' ? 'selected' : ''}`}
						onClick={() => setStyles({ ...styles, button: 'square' })}
					>
						Square
					</button>
					<button
						type="button"
						className={`form-button button-square themed-inverse-button block w-32 ${styles.button === 'inverse-square' ? 'selected' : ''}`}
						onClick={() =>
							setStyles({
								...styles,
								button: 'inverse-square',
							})
						}
					>
						Square
					</button>
					<button
						type="button"
						className={`form-button button-rounded-rectangle themed-button w-32 block mt-2 ${styles.button === 'rounded-rectangle' || !styles.button ? 'selected' : ''}`}
						onClick={() =>
							setStyles({
								...styles,
								button: 'rounded-rectangle',
							})
						}
					>
						Radius
					</button>
					<button
						type="button"
						className={`form-button button-rounded-rectangle themed-inverse-button w-32 block mt-2 ${styles.button === 'inverse-rounded-rectangle' ? 'selected' : ''}`}
						onClick={() =>
							setStyles({
								...styles,
								button: 'inverse-rounded-rectangle',
							})
						}
					>
						Radius
					</button>
					<button
						type="button"
						className={`form-button button-pill themed-button w-32 block mt-2 ${styles.button === 'pill' ? 'selected' : ''}`}
						onClick={() => setStyles({ ...styles, button: 'pill' })}
					>
						Pill
					</button>
					<button
						type="button"
						className={`form-button button-pill themed-inverse-button w-32 block mt-2 ${styles.button === 'inverse-pill' ? 'selected' : ''}`}
						onClick={() =>
							setStyles({ ...styles, button: 'inverse-pill' })
						}
					>
						Pill
					</button>
				</div>
			</StyleSection>
		</ThemedForm>
	);
};

export const StylesAdvanced = ({
	styles,
	setStyles,
	customCssWarnings,
	customCssEnabled,
}) => {
	return (
		<ThemedForm styles={styles} onlyColor={true}>
			{!!customCssEnabled && (
				<CustomCssEditor
					styles={styles}
					setStyles={setStyles}
					warnings={customCssWarnings}
				/>
			)}
		</ThemedForm>
	);
};
